import { useEffect, useRef, useState } from 'react';

// The PDF batch server in ../prakriti-pdf-batch. In development (`npm start` here) it runs
// on port 4000; when hosted, its address comes from public/config.js.

const API_BASE = process.env.NODE_ENV === 'development'
// ? `http://localhost:4004`
  ? `https://jivaapireport.tactionclients.com` 
  : (window.PRAKRITI_API_BASE || '').replace(/\/+$/, '');

const buttonStyle = {
  padding: '6px 12px',
  borderRadius: 6,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'sans-serif',
  fontWeight: 600,
};

// Upload button for the top bar: sends an Excel sheet of Prakriti results to the batch
// server, which makes one report PDF per student. Progress and the finished PDFs are
// shown in a side panel.
export default function ExcelUpload() {
  const inputRef = useRef(null);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const running = job?.status === 'running';

  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/jobs/${job.id}`);
        if (res.ok) setJob(await res.json());
      } catch {
        // Server briefly unreachable; keep polling.
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [running, job?.id]);

  async function upload(file) {
    setError('');
    setOpen(true);
    try {
      const res = await fetch(`${API_BASE}/api/jobs`, {
        method: 'POST',
        headers: { 'X-File-Name': encodeURIComponent(file.name) },
        body: file,
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `Upload failed (${res.status})`);
      setJob(body);
    } catch (err) {
      setError(err.message === 'Failed to fetch'
        ? 'Cannot reach the PDF server. If running locally, start it with: cd prakriti-pdf-batch && npm start'
        : err.message);
    }
  }

  const percent = job?.total ? Math.round((job.done / job.total) * 100) : 0;
  const okCount = job?.students?.filter((s) => s.status === 'ok').length ?? 0;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files[0];
          e.target.value = '';
          if (file) upload(file);
        }}
      />
      <button
        onClick={() => inputRef.current.click()}
        disabled={running}
        style={{ ...buttonStyle, background: '#E0A95C', color: '#2A2520', opacity: running ? 0.6 : 1 }}
      >
        {running ? `Making PDFs… ${job.done}/${job.total || '?'}` : 'Upload Excel'}
      </button>
      {(job || error) && !open && (
        <button onClick={() => setOpen(true)} style={{ ...buttonStyle, background: 'transparent', color: '#fff', border: '1px solid #fff' }}>
          Show PDFs
        </button>
      )}

      {open && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(460px, 100vw)', background: '#fff',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.25)', zIndex: 10, display: 'flex', flexDirection: 'column',
          fontFamily: 'sans-serif', color: '#2A2520',
        }}>
          <div style={{ padding: '12px 16px', background: '#2A2520', color: '#fff', display: 'flex', alignItems: 'center' }}>
            <strong style={{ flex: 1 }}>Report PDFs from Excel</strong>
            <button onClick={() => setOpen(false)} style={{ ...buttonStyle, background: '#fff', color: '#2A2520' }}>Close</button>
          </div>

          <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
            {error && <p style={{ color: '#B3261E', marginTop: 0 }}>{error}</p>}

            {job && (
              <>
                <p style={{ marginTop: 0 }}>
                  <strong>{job.fileName}</strong><br />
                  {running && `Making PDFs: ${job.done} of ${job.total || '?'} students`}
                  {job.status === 'done' && `Done: ${okCount} PDFs made.`}
                  {job.status === 'done-with-errors' && `Finished: ${okCount} PDFs made, some rows had errors (see below).`}
                  {job.status === 'failed' && 'The job failed. See the log below.'}
                </p>
                {running && (
                  <div style={{ height: 8, background: '#EFE6D8', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
                    <div style={{ width: `${percent}%`, height: '100%', background: '#BD4A1E', transition: 'width 0.5s' }} />
                  </div>
                )}

                {job.students && (
                  <>
                    <p style={{ fontSize: 13, color: '#6B5B4E' }}>
                      Saved in: <code style={{ wordBreak: 'break-all' }}>{job.outDir}</code>
                      {job.summaryUrl && (<><br /><a href={`${API_BASE}${job.summaryUrl}`} target="_blank" rel="noreferrer">summary.csv</a></>)}
                    </p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                          <th style={{ padding: 4 }}>Student</th>
                          <th style={{ padding: 4 }}>Result</th>
                          <th style={{ padding: 4 }}>PDF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {job.students.map((s, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: 4 }}>{s.name || '(no name)'}<br /><span style={{ color: '#8a7a6e' }}>{s.admissionNo}</span></td>
                            <td style={{ padding: 4 }}>{s.result}</td>
                            <td style={{ padding: 4 }}>
                              {s.url
                                ? <a href={`${API_BASE}${s.url}`} target="_blank" rel="noreferrer">Open</a>
                                : <span style={{ color: '#B3261E' }}>{s.status}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                <details style={{ marginTop: 12 }} open={job.status === 'failed'}>
                  <summary style={{ cursor: 'pointer' }}>Log</summary>
                  <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', background: '#f7f4ef', padding: 8 }}>{job.log.join('\n')}</pre>
                </details>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
